import axios from 'axios';
import * as indexedDB from '../utils/indexedDB';
import * as tokenEncryption from '../utils/tokenEncryption';

// Get base URL from environment - consistent with apiClient.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`, // Append /api to be consistent with apiClient.js
  timeout: 30000,
  withCredentials: true,
});

// Helper function to get token from storage (same as apiClient.js)
const getTokenFromStorage = async (key) => {
  try {
    // Try IndexedDB first
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem(key);

      if (encryptedToken) {
        try {
          const decryptedToken = await tokenEncryption.decryptToken(encryptedToken);
          if (decryptedToken) {
            return decryptedToken;
          }
        } catch (decryptError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[LocationAccessService] Decryption failed:', decryptError);
          }
        }
      }
    }

    // Fallback to localStorage
    const localToken = localStorage.getItem(key);
    if (localToken) {
      return localToken;
    }

    return null;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[LocationAccessService] Error retrieving token:', error);
    }
    // Last resort
    return localStorage.getItem(key);
  }
};

// Add request interceptor to include auth token (async for IndexedDB)
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await getTokenFromStorage('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Service for handling location access-related API interactions
 */
class LocationAccessService {
  /**
   * Get all location access entries for a specific user
   * @param {number} userId - User ID
   * @returns {Promise<Array>} Array of location access objects
   */
  async getUserLocationAccess(userId) {
    try {
      const response = await axiosInstance.get(`/location-access/user/${userId}`);
      return response.data.success ? response.data.data : [];
    } catch (error) {
      // Check if endpoint doesn't exist (404)
      if (error.response?.status === 404 || error.response?.data?.error === 'Endpoint not found') {
        console.warn('[LocationAccessService] location-access endpoint not available, returning empty array');
        return [];
      }
      // For other errors, log but don't throw - return empty array
      console.error('[LocationAccessService] Error fetching user location access:', error.message);
      return [];
    }
  }

  /**
   * Get all users with access to a specific location
   * @param {number} locationId - Location ID
   * @returns {Promise<Array>} Array of location access objects
   */
  async getLocationUsers(locationId) {
    try {
      const response = await axiosInstance.get(`/location-access/location/${locationId}`);
      return response.data.success ? response.data.data : [];
    } catch (error) {
      this.handleError(error);
      return [];
    }
  }

  /**
   * Grant access to a location for a user
   * @param {Object} accessData - Access data
   * @param {number} accessData.userId - User ID
   * @param {number} accessData.locationId - Location ID
   * @param {string} accessData.accessLevel - Access level ('view', 'edit', 'admin')
   * @param {Date} [accessData.expiresAt] - Optional expiration date
   * @returns {Promise<Object>} Created or updated location access object
   */
  async grantAccess(accessData) {
    try {
      const response = await axiosInstance.post('/location-access', accessData);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Update location access
   * @param {number} accessId - Location access ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated location access object
   */
  async updateAccess(accessId, updateData) {
    try {
      const response = await axiosInstance.put(`/location-access/${accessId}`, updateData);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Revoke location access
   * @param {number} accessId - Location access ID
   * @returns {Promise<Object>} Revocation result
   */
  async revokeAccess(accessId) {
    try {
      const response = await axiosInstance.delete(`/location-access/${accessId}`);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Bulk revoke location access
   * @param {Array<number>} accessIds - Array of location access IDs
   * @returns {Promise<Object>} Bulk revocation result
   */
  async bulkRevokeAccess(accessIds) {
    try {
      const response = await axiosInstance.post('/location-access/bulk-revoke', {
        ids: accessIds
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Get all location access entries with pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Paginated location access data
   */
  async getAllLocationAccess(params = {}) {
    try {
      const response = await axiosInstance.get('/location-access', { params });
      return response.data;
    } catch (error) {
      this.handleError(error);
      return { success: false, data: [], pagination: {} };
    }
  }

  /**
   * Handle API errors
   * @private
   * @param {Error} error - Error object
   * @throws {Error} Processed error with meaningful message
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data.error || error.response.data.message || 'An error occurred';
      throw new Error(`Location Access Service Error: ${message}`);
    } else if (error.request) {
      // Request made but no response received
      throw new Error('Location Access Service Error: No response from server');
    } else {
      // Error in request setup
      throw new Error(`Location Access Service Error: ${error.message}`);
    }
  }
}

// Create singleton instance
const locationAccessService = new LocationAccessService();

// Export default
export default locationAccessService;
